/* eslint-disable */
import React, { useContext, useState } from 'react';
import { UserContext } from '../../../App';
import styles from './UpsellCards.module.css';
import dataLayerPush from '../../../utils/data-layer';

/**
 * Composant partagé pour les cartes d'upsell (toggle ou quantity).
 *
 * Props :
 *  - config        : Array d'objets upsell (key, label, desc, hsDesc, price, unit, suffix, type, billing, sku)
 *  - upsellQty     : état { [key]: boolean|number }
 *  - setUpsellQty  : setter
 *  - onTotalChange : callback(totalUpsells) appelé à chaque changement (optionnel)
 */
function UpsellCards({ config, upsellQty, setUpsellQty, onTotalChange }) {
    const { company, handleChange, addNamedLineItem, deleteLineItem, updateLineItem } = useContext(UserContext);
    const [busyKeys, setBusyKeys] = useState({});

    const hubspotPath = (key) => `hubspot_product.one_off.${key}`;

    async function syncToHubspot(upsell, totalPrice, qty) {
        const path         = hubspotPath(upsell.key);
        const existingItem = company.hubspot_product?.one_off?.[upsell.key]?.line_item;
        const quantity     = qty || (upsell.type === 'toggle' ? 1 : Math.round(totalPrice / upsell.price));

        setBusyKeys(prev => ({ ...prev, [upsell.key]: true }));
        try {
            if (totalPrice > 0) {
                if (existingItem) {
                    const updateProps = { price: upsell.price, quantity: quantity };
                    if (upsell.billing) updateProps.recurringbillingfrequency = upsell.billing;
                    await updateLineItem(existingItem, updateProps);
                } else {
                    const extraProps = { hs_sku: upsell.sku, description: upsell.hsDesc, ...(upsell.extraHsProps || {}) };
                    if (upsell.billing) extraProps.recurringbillingfrequency = upsell.billing;
                    await addNamedLineItem(upsell.label, upsell.price, path, quantity, extraProps);
                }
            } else if (existingItem) {
                await deleteLineItem(existingItem, path);
            }
        } finally {
            setBusyKeys(prev => ({ ...prev, [upsell.key]: false }));
        }
    }

    function toggleUpsell(upsell) {
        if (busyKeys[upsell.key]) return;
        const newVal = !upsellQty[upsell.key];
        const price  = newVal ? upsell.price : 0;
        setUpsellQty(prev => ({ ...prev, [upsell.key]: newVal }));
        handleChange({ name: `shoppingCart.${upsell.key}`, value: newVal ? upsell.price : null });
        syncToHubspot(upsell, price, newVal ? 1 : 0);
        dataLayerPush({ event: newVal ? 'add_to_cart' : 'remove_from_cart', item: upsell.key, price: upsell.price });
    }

    function changeQty(upsell, delta) {
        if (busyKeys[upsell.key]) return;
        const newQty   = Math.max(0, (upsellQty[upsell.key] || 0) + delta);
        const newPrice = newQty * upsell.price;
        setUpsellQty(prev => ({ ...prev, [upsell.key]: newQty }));
        handleChange({ name: `shoppingCart.${upsell.key}`, value: newQty > 0 ? newPrice : null });
        syncToHubspot(upsell, newPrice, newQty);
        if (delta > 0) dataLayerPush({ event: 'add_to_cart', item: upsell.key, price: upsell.price });
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>Options complémentaires</div>

            {config.map(upsell => {
                const qty      = upsellQty[upsell.key];
                const selected = upsell.type === 'toggle' ? !!qty : qty > 0;

                return (
                    <div key={upsell.key} className={`${styles.card} ${selected ? styles.cardSelected : ''}`}>
                        <div className={styles.cardLeft}>
                            <div className={styles.info}>
                                <div className={styles.label}>{upsell.label}</div>
                                <div className={styles.desc}>{upsell.desc}</div>
                            </div>
                        </div>

                        <div className={styles.cardRight}>
                            <div className={styles.priceTag}>
                                <span className={styles.priceValue}>
                                    {upsell.price.toLocaleString('fr-FR')}
                                </span>
                                <span className={styles.priceUnit}>{upsell.unit}{upsell.suffix}</span>
                            </div>

                            {upsell.type === 'toggle' ? (
                                <button
                                    className={`${styles.btn} ${selected ? styles.btnRemove : styles.btnAdd}`}
                                    onClick={() => toggleUpsell(upsell)}
                                    disabled={!!busyKeys[upsell.key]}
                                    style={busyKeys[upsell.key] ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                >
                                    {busyKeys[upsell.key] ? '...' : selected ? '✓ Ajouté' : '+ Ajouter'}
                                </button>
                            ) : (
                                <div className={styles.qtyControls}>
                                    <button
                                        className={styles.qtyBtn}
                                        onClick={() => changeQty(upsell, -1)}
                                        disabled={qty === 0 || !!busyKeys[upsell.key]}
                                        style={busyKeys[upsell.key] ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                        aria-label="Diminuer"
                                    >−</button>
                                    <span className={styles.qtyValue}>{busyKeys[upsell.key] ? '...' : qty}</span>
                                    <button
                                        className={styles.qtyBtn}
                                        onClick={() => changeQty(upsell, +1)}
                                        disabled={!!busyKeys[upsell.key]}
                                        style={busyKeys[upsell.key] ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                        aria-label="Augmenter"
                                    >+</button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default UpsellCards;
